import { useId } from 'react';

/**
 * ADR signet — solid left half, plissé right half, inline SVG so the
 * `<clipPath>` ID can be unique per instance via React's `useId()`.
 * Without the unique ID, multiple logos on the same page would mask
 * one another (charter §2 + maquettes README).
 *
 * Default 26×32 to match the maquettes' header sizing.
 */
export default function Logo({
  size = 26,
  className = '',
  ariaHidden = true,
}: {
  size?: number;
  className?: string;
  ariaHidden?: boolean;
}) {
  const id = useId();
  const clipId = `adr-clip-${id.replace(/:/g, '')}`;
  const height = (size * 84) / 70;
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 70 84"
      className={className}
      role={ariaHidden ? undefined : 'img'}
      aria-hidden={ariaHidden || undefined}
      aria-label={ariaHidden ? undefined : 'ADR — Analyse de Dossier Répressif'}
    >
      <defs>
        <clipPath id={clipId}>
          <rect x="0" y="0" width="35" height="84" />
        </clipPath>
      </defs>
      <path d="M 0 0 L 70 0 L 70 84 L 35 70 L 0 84 Z" fill="#1A1A1A" clipPath={`url(#${clipId})`} />
      <path
        d="M 0 0 L 70 0 L 70 84 L 35 70 L 0 84 Z"
        fill="none"
        stroke="#1A1A1A"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M 42 5 Q 40 26, 42 47 T 42 78"
        fill="none"
        stroke="#1A1A1A"
        strokeWidth="1"
        opacity="0.8"
      />
      <path
        d="M 52 5 Q 54 26, 52 47 T 52 78"
        fill="none"
        stroke="#1A1A1A"
        strokeWidth="1"
        opacity="0.8"
      />
      <path
        d="M 62 5 Q 60 26, 62 47 T 62 78"
        fill="none"
        stroke="#1A1A1A"
        strokeWidth="1"
        opacity="0.8"
      />
    </svg>
  );
}

/** Logo + wordmark Georgia + sous-titre italique (cf. maquettes header). */
export function LogoLockup({
  size = 26,
  showSubtitle = true,
  className = '',
}: {
  size?: number;
  showSubtitle?: boolean;
  className?: string;
}) {
  return (
    <div className={`inline-flex items-center gap-2.5 text-text-primary ${className}`}>
      <Logo size={size} />
      <span className="flex flex-col leading-[1.1]">
        <span className="font-serif text-[20px] font-medium leading-none tracking-[1.5px]">
          ADR
        </span>
        {showSubtitle && (
          <span className="mt-0.5 font-serif text-[10px] italic leading-none tracking-[0.2px] text-text-secondary">
            Analyse de Dossier Répressif
          </span>
        )}
      </span>
    </div>
  );
}
